import { ArrowRight, FileText, HelpCircle, Loader2, PenLine, Replace, Trash2 } from "lucide-react";
import type { ComponentType } from "react";

import { Button } from "@/components/ui/button";
import type { DeckRevision, SlideEdit } from "@/lib/api";
import { cn } from "@/lib/utils";

import type { Awaiting } from "./types";

interface DeckRevisionReviewProps {
  revision: DeckRevision;
  /**
   * Only `"revision_confirmation"` is an offer. Anything else — in practice a
   * revision that resolved no edit — is a question, and must not carry a button
   * whose press would rebuild an identical deck and look like the edit landed.
   */
  awaiting: Awaiting;
  onConfirm: () => void;
  isRebuilding: boolean;
}

interface ActionMeta {
  /** What this row did, in the requestor's language. */
  verb: string;
  icon: ComponentType<{ className?: string }>;
  accent: string;
}

const ACTION_META: Record<SlideEdit["action"], ActionMeta> = {
  remove: { verb: "Removed", icon: Trash2, accent: "text-outcome-escalated" },
  retitle: { verb: "New title", icon: PenLine, accent: "text-velux" },
  replace_text: { verb: "Replacement text", icon: Replace, accent: "text-velux" },
};

/**
 * The edits to a deck that already exists, offered before anything is rebuilt.
 *
 * The valuable half of this panel is what it does *not* list. A market asking
 * for slide 4 to go is not asking for the other fourteen slides to be
 * rewritten, and before this route existed that is exactly what happened — the
 * follow-up silently recomposed the whole deck. So the changes are enumerated
 * one per row, the slide count is stated before and after, and anything the
 * composer could not pin to a single slide is quoted back as a question rather
 * than guessed at.
 *
 * When nothing actionable resolved there is no button at all: confirming would
 * rebuild the same deck and read as a change that was made.
 */
export function DeckRevisionReview({
  revision,
  awaiting,
  onConfirm,
  isRebuilding,
}: DeckRevisionReviewProps) {
  const edits = revision.edits ?? [];
  const unsupported = revision.unsupported ?? [];
  const removes = edits.some((edit) => edit.action === "remove");
  const source = revision.source_filename || "the deck you have";
  // Both halves are checked on purpose. `awaiting` is the backend's signal, and
  // the empty-edit guard makes the rule local: no edits, no button, ever.
  const canConfirm = awaiting === "revision_confirmation" && edits.length > 0;

  return (
    <section className="border-border bg-surface-muted/60 space-y-4 rounded-lg border p-4">
      <header className="space-y-1">
        <h3 className="font-heading text-foreground text-sm font-bold">
          {edits.length > 0
            ? `${edits.length} ${edits.length === 1 ? "change" : "changes"} to ${source}`
            : `No change I can pin to a slide of ${source}`}
        </h3>
        <p className="text-muted text-xs leading-5">
          {edits.length > 0
            ? "Every other slide stays exactly as it is — same copy, same images, same template."
            : "Nothing has been rebuilt, and nothing will be until you name the slide."}
        </p>
      </header>

      {edits.length > 0 && (
        <ul className="space-y-1.5">
          {edits.map((edit, index) => (
            <EditRow key={`${edit.slide_index}-${edit.action}-${index}`} edit={edit} />
          ))}
        </ul>
      )}

      {/* Only a removal changes the length of the deck, and that is the one
          consequence someone may not have intended when they named a slide. */}
      {removes && (
        <p className="text-muted text-xs">
          That takes the deck from{" "}
          <span className="text-foreground font-semibold tabular-nums">
            {revision.slides_before ?? 0}
          </span>{" "}
          slides to{" "}
          <span className="text-foreground font-semibold tabular-nums">
            {revision.slides_after ?? 0}
          </span>
          .
        </p>
      )}

      {unsupported.length > 0 && (
        <div className="border-border bg-card flex items-start gap-2.5 rounded-md border p-3">
          <HelpCircle className="text-muted mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="text-foreground text-xs leading-5">
            {edits.length > 0
              ? `I also could not tell which slide ${quoteList(unsupported)} means, so I left it alone — which slide is it?`
              : `Which slide is ${quoteList(unsupported)}?`}{" "}
            <span className="text-muted">
              Name it by number — &quot;remove slide 4&quot;, &quot;change the title of slide 2
              to…&quot; — and I&apos;ll make just that change.
            </span>
          </p>
        </div>
      )}

      {canConfirm && (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={onConfirm} disabled={isRebuilding}>
            {isRebuilding ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Rebuilding the deck…
              </>
            ) : (
              <>
                <PenLine className="mr-1.5 h-3.5 w-3.5" />
                Make {edits.length === 1 ? "this change" : "these changes"}
              </>
            )}
          </Button>
          {!isRebuilding && (
            <p className="text-muted text-xs">Or tell me what else to change below.</p>
          )}
        </div>
      )}

      {isRebuilding && (
        <p className="text-muted flex items-center gap-2 text-xs">
          <FileText className="h-3.5 w-3.5" />
          Reopening the slides that were built and applying only these edits. Nothing is composed
          again.
        </p>
      )}
    </section>
  );
}

/** One slide, and what happens to it — a removal struck through, a change as before → after. */
function EditRow({ edit }: { edit: SlideEdit }) {
  const meta = ACTION_META[edit.action];
  const Icon = meta.icon;
  const title = edit.slide_title || "this slide";

  return (
    <li className="border-border bg-card flex items-start gap-3 rounded-md border px-3 py-2">
      <span className="text-muted mt-0.5 w-4 shrink-0 text-right text-xs tabular-nums">
        {edit.slide_index}
      </span>
      <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", meta.accent)} />

      <div className="min-w-0 flex-1">
        {edit.action === "remove" ? (
          <p className="text-muted decoration-outcome-escalated/70 text-sm line-through decoration-2">
            {title}
          </p>
        ) : (
          <p className="text-foreground text-sm break-words">
            {edit.before ? (
              <span className="text-muted decoration-muted/50 line-through">{edit.before}</span>
            ) : (
              <span className="text-muted italic">nothing there yet</span>
            )}
            <ArrowRight className="text-faint mx-1.5 inline h-3 w-3 shrink-0 align-[-1px]" />
            <span>{edit.detail || <span className="text-muted italic">(cleared)</span>}</span>
          </p>
        )}
        {/* The slide number is already in the gutter, so this says only what
            kind of change it is. */}
        <p className="text-muted mt-0.5 text-[11px]">{meta.verb}</p>
      </div>
    </li>
  );
}

/**
 * The phrases the composer could not place, quoted in the requestor's own words.
 *
 * Built as a string rather than as JSX so the curly quotes do not have to be
 * escaped into unreadability — and so the phrasing stays one sentence.
 */
function quoteList(phrases: string[]): string {
  const quoted = phrases.map((phrase) => `“${phrase}”`);
  const last = quoted.at(-1) ?? "";
  if (quoted.length < 2) {
    return last;
  }
  return `${quoted.slice(0, -1).join(", ")} or ${last}`;
}
